'use client'
import type { FC, ReactNode } from 'react'
import NextLink, { type LinkProps } from 'next/link'
import { useParams, useRouter } from 'next/navigation'
import { usePageTransition } from '@/hooks'
import { LOCALE } from '@/utils'
import type { TypeLang } from '@/schema'

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

interface Props extends LinkProps {
  href: string,
  className?: string
  children: ReactNode
  title?: string
  rel?: 'alternate' | 'author' | 'bookmark' | 'help' | 'license' | 'me' | 'next' | 'prev' | 'search' | 'tag'
}

const getHref = (href, lang) => {
  const locale = lang ?? LOCALE

  const isDefaultLocale = locale === LOCALE
  const isRootPath = href === '/'

  if (isRootPath) {
    return isDefaultLocale ? '/' : `/${locale}`
  }

  return `${isDefaultLocale ? '' : `/${locale}`}${href.startsWith('/') ? '' : '/'}${href}`
}

export const Link: FC<Props> = ({ href, rel, children, className, title, ...props }) => {
  const { lang }: { lang: TypeLang } = useParams()
  const hrefWithLang = getHref(href, lang)
  const { setTransitionName, setCortinaTransitionName, setTargetHref } = usePageTransition()
  const { push } = useRouter()
  const onClick = async (e) => {
    e.preventDefault()

    // Fade out current page
    setTransitionName('exit')
    props.onClick?.(e)
    setCortinaTransitionName('covering') // start Cortina (scaleY: 0 → 1)
    setTargetHref(hrefWithLang)
    // Wait for Cortina to cover the page
    // if (hrefWithLang.includes('contact') || hrefWithLang.includes('wheretobuy')) {
    await sleep(800)
    // }   // wait for Cortina to cover the page
    // Navigate to new page
    push(hrefWithLang)
  }

  return (
    <NextLink href={hrefWithLang} {...rel && { rel }} {...className && { className }} {...props} {...title && { title }} onClick={onClick}>
      {children}
    </NextLink>
  )
}
